<template>
  <div class="enderecos-container">
    <h1><i class="fas fa-map-marker-alt"></i> {{ $t('address.info_title') }}</h1>

    <!-- Loader com prioridade -->
    <div v-if="isLoading" class="loading">
      <i class="fas fa-spinner fa-spin"></i> {{ $t('address.loading') }}
    </div>

    <!-- Conteúdo após carregar -->
    <div v-else>
      <div v-if="enderecos.length" class="enderecos-list">
        <div v-for="(endereco, index) in enderecos" :key="index" class="endereco-row">
          <!-- Badge de Principal -->
          <div v-if="endereco.principal" class="principal-badge">
            <i class="fas fa-star"></i> {{ $t('address.main_address') }}
          </div>

          <!-- Informações do endereço -->
          <div class="endereco-link">
            <div class="endereco-detail">
              <strong class="endereco-title">{{ endereco.nome || $t('address.no_name') }}</strong>
            </div>
            <div class="endereco-detail" v-if="isBrazilian">
              <strong>{{ $t('address.zip_code') }}</strong>
              <span :class="{ 'not-provided': !endereco.cep }">{{ endereco.cep || $t('address.not_informed') }}</span>
            </div>

            <div class="endereco-detail">
              <strong>{{ $t('address.street') }}</strong>
              <span :class="{ 'not-provided': !endereco.logradouro }">{{ endereco.logradouro ||
                $t('address.not_informed') }}</span>
            </div>
            <div class="endereco-detail">
              <strong>{{ $t('address.number') }}</strong>
              <span :class="{ 'not-provided': !endereco.numero }">{{ endereco.numero || $t('address.not_informed')
              }}</span>
            </div>
            <div class="endereco-detail">
              <strong>{{ $t('address.neighborhood') }}</strong>
              <span :class="{ 'not-provided': !endereco.bairro }">{{ endereco.bairro || $t('address.not_informed')
              }}</span>
            </div>
            <div class="endereco-detail">
              <strong>{{ $t('address.city') }}</strong>
              <span :class="{ 'not-provided': !endereco.cidade }">{{ endereco.cidade || $t('address.not_informed')
              }}</span>
            </div>
            <div class="endereco-detail">
              <strong>{{ $t('address.state') }}</strong>
              <span :class="{ 'not-provided': !endereco.estado }">{{ endereco.estado || $t('address.not_informed')
              }}</span>
            </div>
            <div class="endereco-detail">
              <strong>{{ $t('address.country') }}</strong>
              <span :class="{ 'not-provided': !endereco.pais }">{{ endereco.pais || $t('address.not_informed') }}</span>
            </div>
            <div class="endereco-detail">
              <strong>{{ $t('address.complement') }}</strong>
              <span :class="{ 'not-provided': !endereco.complemento }">{{ endereco.complemento ||
                $t('address.not_informed') }}</span>
            </div>
          </div>

          <!-- Botões -->
          <div class="actions-row">
            <button @click="deleteEndereco(endereco.id)" class="remove-button">
              <i class="fas fa-trash-alt"></i> {{ $t('address.remove') }}
            </button>
            <button class="set-main-btn" v-if="!endereco.principal" @click="setEnderecoPrincipal(endereco.id)">
              <i class="fas fa-star"></i> {{ $t('address.set_as_main') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Sem endereços -->
      <div v-else class="no-endereco">
        <p>{{ $t('address.no_address') }}</p>
      </div>
    </div>

    <!-- Botão de adicionar endereço -->
    <div class="actions">
      <button @click="showModal = true" class="add-button">
        <i class="fas fa-plus"></i> {{ $t('address.add_new') }}
      </button>
    </div>

    <AlterarEnderecoModal v-if="showModal" @close="showModal = false" @salvar="handleEnderecoAdicionado" />
  </div>
</template>

<script>
import axios from 'axios';
import AlterarEnderecoModal from './AlterarEnderecoModal.vue';

export default {
  name: 'Enderecos',
  components: {
    AlterarEnderecoModal,
  },
  data() {
    return {
      enderecos: [],
      showModal: false,
      isLoading: true,
    };
  },
  computed: {
    user() {
      return this.$store.getters['auth/getUser'];
    },
    isBrazilian() {
      return this.user?.estrangeiro === 1 || this.user?.estrangeiro === '1';
    }

  },
  methods: {
    async deleteEndereco(id) {
      try {
        this.isLoading = true;
        const token = localStorage.getItem('token');
        if (!token) {
          alert(this.$t('authTokenMissing') || 'Token de autenticação ausente.');
          return;
        }

        const endereco = this.enderecos.find(e => e.id === id);
        if (!endereco) {
          alert(this.$t('address.not_found') || 'Endereço não encontrado.');
          return;
        }

        await axios.put(
          `${process.env.VUE_APP_API_URI}/ecom/cliente/address/${id}`,
          {
            ativo: false,
            nome: endereco.nome || ''
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        this.enderecos = this.enderecos.filter((e) => e.id !== id);
      } catch (error) {
        console.error('Erro ao excluir o endereço:', error);
        alert(this.$t('address.deleted_error') || 'Erro ao excluir o endereço');
      } finally {
        this.isLoading = false;
      }
    },

    async setEnderecoPrincipal(id) {
      try {
        this.isLoading = true;
        const token = localStorage.getItem('token');
        await axios.patch(
          `${process.env.VUE_APP_API_URI}/ecom/cliente/address/${id}/set-main`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        this.fetchEnderecos();
        window.location.reload();
      } catch (error) {
        console.error('Erro ao definir como principal:', error);
        alert(this.$t('address.main_set_error') || 'Erro ao definir como principal');
      } finally {
        this.isLoading = false;
      }
    },
    handleEnderecoAdicionado(novoEndereco) {
      this.enderecos.push(novoEndereco);
      this.showModal = false;
    },
    fetchEnderecos() {
      const user = this.user;
      if (user && Array.isArray(user.enderecos)) {
        this.enderecos = user.enderecos.filter(e => e.ativo);
      } else {
        this.enderecos = [];
      }
      this.isLoading = false;
    },
  },
  watch: {
    user: {
      handler(newUser) {
        if (newUser && Array.isArray(newUser.enderecos)) {
          this.enderecos = newUser.enderecos.filter(e => e.ativo);
        } else {
          this.enderecos = [];
        }
        this.isLoading = false;
      },
      immediate: true,
      deep: true,
    },
  },

};
</script>

<style scoped>
.enderecos-container {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.enderecos-container h1 {
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 0.5rem;
}

.endereco-row {
  padding: 1.2rem;
  margin-bottom: 1rem;
  background-color: #fff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease;
}

.endereco-link {
  display: block;
  text-decoration: none;
  color: inherit;
}

.endereco-title {
  font-size: 1.2rem;
  font-weight: bold;
  color: #2c3e50;
}

.endereco-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  font-size: 1rem;
  color: #34495e;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 0.5rem;
}

.endereco-detail .not-provided {
  color: #e74c3c;
}

.add-button {
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 2rem;
}

.add-button:hover {
  background-color: #2ecc71;
}

.remove-button {
  background-color: #c0392b;
  color: white;
  border: none;
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
  border-radius: 8px;
  cursor: pointer;
}

.remove-button:hover {
  background-color: #e74c3c;
}

.actions {
  text-align: center;
}

.actions-row {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
}

.set-main-btn {
  background: #f39c12;
  color: white;
  border: none;
  padding: 0.6rem 1.5rem;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
}

.set-main-btn:hover {
  background: #e67e22;
}

.principal-badge {
  background-color: #f1c40f;
  color: white;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.no-endereco {
  text-align: center;
  color: #7f8c8d;
  font-size: 1rem;
}

.loading {
  text-align: center;
  color: #3498db;
  font-size: 1.5rem;
  margin-top: 2rem;
}

@media (max-width: 768px) {
  .enderecos-container {
    padding: 1.5rem;
  }

  .endereco-detail {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
